import React from "react";
import {
  Grid,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
  Dialog,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  useTheme,
} from "@mui/material";
import "react-quill/dist/quill.snow.css";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import DateRangeIcon from "@mui/icons-material/DateRange";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CustomTypography from "../../globalStyles/fonts";
import { roundToThree } from "../../utils/utility";
import { disabledFieldSx } from "../../constants/fieldStyles";
import { formatDateTime } from "../../utils/formatDate";

// Helper to format address parts cleanly (filters empty values, trims, joins)
function formatAddress(parts = []) {
  return parts
    .map((p) => (p || "").toString().trim())
    .filter((p) => p && p !== ",")
    .join(", ");
}

const HistoricalShipmentDetails = ({ shipment, open, onClose }) => {
  const theme = useTheme();
  // Helper to format date strings
  const formatDate = (dateStr) => {
    if (!dateStr) return "Not available";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return formatDateTime(dateStr);
  };

  // Helper component for field groups
  const FieldGroup = ({
    title,
    icon,
    children,
    color = theme.palette.primary.main,
  }) => (
    <Card
      elevation={2}
      sx={{
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${color}20`,
        background: `linear-gradient(135deg, ${color}05 0%, ${color}02 100%)`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar
            sx={{
              bgcolor: color,
              mr: 2,
              width: 32,
              height: 32,
            }}
          >
            {icon}
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              color: "#11193F",
              fontWeight: 600,
              fontSize: "1.1rem",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {children}
        </Grid>
      </CardContent>
    </Card>
  );

  // Helper component for individual fields
  const FieldItem = ({ label, value, xs = 12, sm = 6, multiline = false }) => (
    <Grid item xs={xs} sm={sm}>
      <Box mb={1}>
        <Typography
          variant="subtitle2"
          sx={{
            color: "#11193F",
            fontWeight: 500,
            fontSize: "0.875rem",
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        {multiline ? (
          <TextField
            value={value || "Not available"}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ readOnly: true }}
            sx={disabledFieldSx}
          />
        ) : (
          <TextField
            value={value || "Not available"}
            fullWidth
            InputProps={{ readOnly: true }}
            sx={disabledFieldSx}
          />
        )}
      </Box>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          pb: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Historical Shipment Details
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Comprehensive shipment information and tracking details
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, pt: 2, backgroundColor: "#f7f8f9" }}>
        <Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 1 }}>
          {/* Basic Information */}
          <FieldGroup
            title="Basic Information"
            icon={<InfoIcon />}
            color={theme.palette.primary.main}
          >
            <FieldItem label="Shipment ID" value={shipment?.shipment_id} />
            <FieldItem label="Manifest ID" value={shipment?.manifest_id} />
            <FieldItem
              label="Tracking Number"
              value={shipment?.tracking_number}
            />
            <FieldItem
              label="Reference Numbers"
              value={shipment?.reference_numbers}
            />
            <FieldItem label="Client ID" value={shipment?.client_id} />
            <FieldItem
              label="Shipper Account Number"
              value={shipment?.shipper_account_number}
            />
            <FieldItem
              label="Carrier Account Number"
              value={shipment?.carrier_account_number}
            />
            <FieldItem
              label="Invoice Number"
              value={shipment?.invoice_number}
            />
          </FieldGroup>

          {/* Dates */}
          <FieldGroup
            title="Important Dates"
            icon={<DateRangeIcon />}
            color={theme.palette.secondary.main}
          >
            <FieldItem
              label="Ship Date"
              value={formatDate(shipment?.ship_date)}
            />
            <FieldItem
              label="Label Create Date"
              value={formatDate(shipment?.label_create_date)}
            />
            <FieldItem
              label="Manifest Date"
              value={formatDate(shipment?.manifest_date)}
            />
            <FieldItem
              label="Invoice Date"
              value={formatDate(shipment?.invoice_date)}
            />
            <FieldItem
              label="Actual Pickup Date"
              value={formatDate(shipment?.actual_pickup_date)}
            />
            <FieldItem
              label="Actual Delivery Date"
              value={formatDate(shipment?.actual_delivery_date)}
            />
          </FieldGroup>

          {/* Addresses */}
          <FieldGroup
            title="Address Information"
            icon={<LocationOnIcon />}
            color={theme.palette.secondary.main}
          >
            <FieldItem
              label="Shipping Address"
              value={formatAddress([
                `${shipment?.shipping_first_name || ""} ${shipment?.shipping_last_name || ""}`.trim(),
                shipment?.shipping_address1,
                shipment?.shipping_address2,
                shipment?.shipping_city,
                shipment?.shipping_province,
                shipment?.shipping_country,
                shipment?.shipping_zip,
              ])}
              multiline
              xs={12}
            />
            <FieldItem
              label="Consignee Address"
              value={formatAddress([
                shipment?.consignee_name,
                shipment?.consignee_address1,
                shipment?.consignee_address2,
                shipment?.consignee_city,
                shipment?.consignee_province,
                shipment?.consignee_country,
                shipment?.consignee_zip,
              ])}
              multiline
              xs={12}
            />
            <FieldItem
              label="Billing Address"
              value={formatAddress([
                shipment?.billing_name,
                shipment?.billing_address1,
                shipment?.billing_address2,
                shipment?.billing_city,
                shipment?.billing_province,
                shipment?.billing_country,
                shipment?.billing_zip,
              ])}
              multiline
              xs={12}
            />
          </FieldGroup>

          {/* Package Details */}
          <FieldGroup
            title="Package Details"
            icon={<LocalShippingIcon />}
            color={theme.palette.primary.main}
          >
            <FieldItem
              label="Scale Weight"
              value={roundToThree(shipment?.scale_weight)}
            />
            <FieldItem label="Height" value={roundToThree(shipment?.height)} />
            <FieldItem label="Width" value={roundToThree(shipment?.width)} />
            <FieldItem label="Length" value={roundToThree(shipment?.length)} />
            <FieldItem label="Weight Unit" value={shipment?.weight_unit} />
            <FieldItem
              label="Dimension Unit"
              value={shipment?.dimension_unit}
            />
            <FieldItem label="Package Type" value={shipment?.package_type} />
            <FieldItem
              label="Service Level Requested"
              value={shipment?.service_level_requested}
            />
            <FieldItem
              label="Declared Value"
              value={shipment?.declared_value}
            />
            <FieldItem
              label="Special Level Flags"
              value={shipment?.special_level_flags}
            />
            <FieldItem label="Address Type" value={shipment?.address_type} />
            <FieldItem
              label="Billable Weight"
              value={shipment?.billable_weight}
            />
            <FieldItem label="Oversize Flag" value={shipment?.oversize_flag} />
            <FieldItem
              label="Overweight Flag"
              value={shipment?.overweight_flag}
            />
            <FieldItem label="Origin Zip" value={shipment?.origin_zip} />
            <FieldItem
              label="Calculated Zone"
              value={shipment?.calculated_zone}
            />
          </FieldGroup>

          {/* Carrier Information */}
          <FieldGroup
            title="Carrier Information"
            icon={<LocalShippingIcon />}
            color={theme.palette.secondary.main}
          >
            <FieldItem label="Carrier Name" value={shipment?.carrier_name} />
            <FieldItem
              label="Actual Service Level"
              value={shipment?.actual_service_level}
            />
          </FieldGroup>

          {/* Costs and Charges */}
          <FieldGroup
            title="Costs and Charges"
            icon={<MonetizationOnIcon />}
            color={theme.palette.primary.main}
          >
            <FieldItem label="Cost Expected" value={shipment?.cost_expected} />
            <FieldItem
              label="Fuel Surcharge Expected"
              value={shipment?.fuel_surcharge_expected}
            />
            <FieldItem
              label="Rate Group Number"
              value={shipment?.rate_group_number}
            />
            <FieldItem
              label="Expected Additional Cost"
              value={shipment?.expected_additional_cost}
            />
            <FieldItem
              label="Fuel Surcharge Actual"
              value={shipment?.fuel_surcharge_actual}
            />
            <FieldItem label="DAS" value={shipment?.das} />
            <FieldItem label="EDAS" value={shipment?.edas} />
            <FieldItem label="Insurance Fee" value={shipment?.insurance_fee} />
            <FieldItem
              label="Import Export Fee"
              value={shipment?.import_export_fee}
            />
            <FieldItem
              label="Duty And Tax Fee"
              value={shipment?.duty_and_tax_fee}
            />
            <FieldItem label="Hazmat" value={shipment?.hazmat} />
            <FieldItem
              label="Saturday Delivery"
              value={shipment?.saturday_delivery}
            />
            <FieldItem label="Other Charges" value={shipment?.other_charges} />
            <FieldItem
              label="Peak Surcharge"
              value={shipment?.peak_surcharge}
            />
            <FieldItem label="Misc" value={shipment?.misc} />
          </FieldGroup>

          {/* Other */}
          <FieldGroup
            title="Additional Information"
            icon={<MoreHorizIcon />}
            color={theme.palette.secondary.main}
          >
            <FieldItem label="Channel" value={shipment?.channel} />
            <FieldItem
              label="Transportation Direction"
              value={shipment?.transportation_direction}
            />
          </FieldGroup>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: "1px solid #d9d9d9",
          backgroundColor: "#f7f8f9",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 4,
            py: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            "&:hover": {
              background: `linear-gradient(135deg, ${
                theme.palette.primary.dark || theme.palette.primary.main
              } 0%, ${
                theme.palette.secondary.dark || theme.palette.secondary.main
              } 100%)`,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoricalShipmentDetails;
